import React, { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";

const AUTH = "228d7d90-2084-41db-a029-6b2f90e6ab52";
const NBA_API_URL = "https://api.balldontlie.io/v1/";
const MLB_API_URL = "https://statsapi.mlb.com/api/v1/"; // Adjust this if you need the actual MLB teams endpoint

const timeframes = [
  { label: "Last Month", value: 1 },
  { label: "Last 2 Months", value: 2 },
  { label: "Last 3 Months", value: 3 },
  { label: "Last 4 Months", value: 4 },
  { label: "Last 5 Months", value: 5 },
  { label: "Last 6 Months", value: 6 },
  { label: "Last 7 Months", value: 7 },
  { label: "Last 8 Months", value: 8 },
  { label: "Last 9 Months", value: 9 },
  { label: "Last 10 Months", value: 10 },
  { label: "Last 11 Months", value: 11 },
  { label: "Last Year", value: 12 }
];

const StatsChart = ({ league }) => {
  const [data, setData] = useState([]);
  const [teams, setTeams] = useState({ NBA: [], MLB: [] });
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState(1);

  // Fetch teams data for both leagues (NBA and MLB)
  useEffect(() => {
    // Fetch NBA teams
    const TEAMS_URL = NBA_API_URL + 'teams' 
    const headers = {};

    headers["Authorization"] = ` ${AUTH}`;
    

    fetch(TEAMS_URL, {
      method: "GET",
      headers: headers
    })
      .then((response) => response.json())
      .then((data) => {
        const nbaTeams = data.data.map((team) => ({
          id: team.id,
          name: team.full_name
        }));
        setTeams((prevTeams) => ({
          ...prevTeams,
          NBA: nbaTeams
        }));
        // if (selectedTeam === null) setSelectedTeam(nbaTeams[0].id); // Set default team if none selected
      })
      .catch((error) => console.error("Error fetching NBA teams:", error));

    // Fetch MLB teams
    fetch(MLB_API_URL + 'teams')
      .then((response) => response.json())
      .then((data) => {
        const mlbTeams = data.teams.map((team) => ({
          id: team.id,
          name: team.name
        }));
        setTeams((prevTeams) => ({
          ...prevTeams,
          MLB: mlbTeams
        }));
        // if (selectedTeam === null) setSelectedTeam(mlbTeams[0].id); // Set default team if none selected
      })
      .catch((error) => console.error("Error fetching MLB teams:", error));
  }, []); // Only fetch teams once on component mount

  // Fetch game data when league, team, or timeframe changes
  useEffect(() => {
    if (!selectedTeam) return;

    const now = new Date();
    const pastDate = new Date();
    pastDate.setMonth(now.getMonth() - selectedTimeframe);
    const startDate = pastDate.toISOString().split("T")[0];
    const endDate = now.toISOString().split("T")[0];

    const API_URL =
      league === "NBA"
        ? `${NBA_API_URL}games?team_ids[]=${selectedTeam}&start_date=${startDate}&end_date=${endDate}`
        : `${MLB_API_URL}schedule/games/?sportId=1&season=2024&teamId=${selectedTeam}&startDate=${startDate}&endDate=${endDate}`;

    console.log(`Fetching data from: ${API_URL}`); // Log the API URL

    const headers = {};
    if (league === "NBA") {
      headers["Authorization"] = ` ${AUTH}`;
    }

    fetch(API_URL, {
      method: "GET",
      headers: headers
    })
      .then((response) => response.json())
      .then((json) => {
        console.log("API Response:", json); // Log the response

        const formattedData =
          league === "NBA"
            ? json.data.map((game) => ({
                date: game.date.split("T")[0],
                points:
                  game.home_team.id === selectedTeam
                    ? game.home_team_score
                    : game.visitor_team_score,
              }))
            : json.dates.flatMap((date) =>
                date.games.map((game) => ({
                  date: game.officialDate,
                  runs:
                    game.teams.home.team.id === selectedTeam
                      ? game.teams.home.score
                      : game.teams.away.score,
                }))
              );

        setData(formattedData);
      })
      .catch((error) => console.error("Error fetching data:", error));
  }, [league, selectedTeam, selectedTimeframe]); // Added selectedTeam as a dependency

  return (
    <div>
      <div>
        <label>Team: </label>
        <select onChange={(e) => setSelectedTeam(Number(e.target.value))} value={selectedTeam}>
          {teams[league].map((team) => (
            <option key={team.id} value={team.id}>{team.name}</option>
          ))}
        </select>
        
        <label> Timeframe: </label>
        <select onChange={(e) => setSelectedTimeframe(Number(e.target.value))} value={selectedTimeframe}>
          {timeframes.map((time) => (
            <option key={time.value} value={time.value}>{time.label}</option>
          ))}
        </select>
      </div>
      
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Line type="monotone" dataKey={league === "NBA" ? "points" : "runs"} stroke="#8884d8" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default StatsChart;
